import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Shield, Heart, UserCheck, ScrollText } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import TncBody from '@/components/TncBody';


export default function TermsAgreementPage({ onAgreed }) {
  const { token, API } = useAuth();
  const { lang } = useLang();
  const [check1, setCheck1] = useState(false);
  const [check2, setCheck2] = useState(false);
  const [check3, setCheck3] = useState(false);
  const [showTnc, setShowTnc] = useState(false);
  const [loading, setLoading] = useState(false);

  const allChecked = check1 && check2 && check3;

  const handleAgree = async () => {
    if (!allChecked) return;
    setLoading(true);
    try {
      await axios.post(`${API}/members/agree-terms`, {
        agreed_terms: true, agreed_background: true, agreed_unmarried: true
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(lang === 'en' ? 'Welcome to Match Group Indonesia!' : 'Selamat datang di Match Group Indonesia!');
      onAgreed();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
    setLoading(false);
  };

  const Checkbox = ({ checked, onChange, children, testId }) => (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      data-testid={testId}
      role="checkbox"
      aria-checked={checked}
      className="w-full text-left flex items-start gap-3 cursor-pointer p-4 rounded-xl glass-card hover:bg-[var(--bg-input)] transition"
    >
      <div className={`w-5 h-5 rounded-md border-2 flex-shrink-0 mt-0.5 flex items-center justify-center transition-all ${checked ? 'bg-[#D98A94] border-[#D98A94]' : 't-border'}`}>
        {checked && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
      </div>
      <span className="t-text text-sm leading-relaxed">{children}</span>
    </button>
  );

  return (
    <div className="min-h-screen t-bg flex items-center justify-center p-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-[#D98A94]/10 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-[#D98A94]" />
          </div>
          <h1 className="text-2xl font-bold t-text mb-2">{lang === 'en' ? 'Before You Begin' : 'Sebelum Anda Mulai'}</h1>
          <p className="t-text-muted text-sm">{lang === 'en' ? 'Please read and agree to the following declarations' : 'Silakan baca dan setujui pernyataan berikut'}</p>
        </div>

        <div className="space-y-3 mb-6">
          <Checkbox checked={check1} onChange={setCheck1} testId="terms-check-1">
            {lang === 'en' ? (
              <>I have read and agree to the applicable <span
                role="link"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setShowTnc(true); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); setShowTnc(true); } }}
                className="text-[#D98A94] underline font-semibold cursor-pointer"
                data-testid="terms-link-en"
              >Terms and Conditions</span>, <span className="font-semibold t-text">including automatic publication of my profile on the official Match Group Indonesia TikTok account</span> (photo, age, domicile, education, occupation only — no WhatsApp number, no full address, no NIK/KTP).</>
            ) : (
              <>Saya telah membaca dan menyetujui <span
                role="link"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setShowTnc(true); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); setShowTnc(true); } }}
                className="text-[#D98A94] underline font-semibold cursor-pointer"
                data-testid="terms-link-id"
              >Syarat dan Ketentuan</span> yang berlaku, <span className="font-semibold t-text">termasuk publikasi profil saya secara otomatis di akun TikTok resmi Match Group Indonesia</span> (hanya foto, usia, domisili, pendidikan, dan pekerjaan — TIDAK termasuk nomor WhatsApp, alamat lengkap, atau NIK/KTP).</>
            )}
          </Checkbox>

          <Checkbox checked={check2} onChange={setCheck2} testId="terms-check-2">
            {lang === 'en'
              ? 'I truthfully declare my background (religion/identity) according to the selected match category.'
              : 'Saya dengan jujur menyatakan latar belakang saya (agama/identitas) sesuai dengan kategori match yang dipilih.'}
          </Checkbox>

          <Checkbox checked={check3} onChange={setCheck3} testId="terms-check-3">
            {lang === 'en'
              ? 'I truthfully declare that I am unmarried, not bound to anyone else, and legally permitted to seek a partner.'
              : 'Saya dengan jujur menyatakan bahwa saya belum menikah, tidak terikat dengan orang lain, dan secara hukum diperbolehkan mencari pasangan.'}
          </Checkbox>
        </div>

        <Button onClick={handleAgree} disabled={!allChecked || loading} data-testid="terms-agree-btn"
          className="w-full bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full h-12 text-base font-medium btn-glow disabled:opacity-40">
          {loading ? '...' : (lang === 'en' ? 'I Agree & Continue' : 'Saya Setuju & Lanjutkan')}
        </Button>

        <p className="mt-6 text-xs t-text-muted text-center leading-relaxed" data-testid="terms-category-note">
          {lang === 'en'
            ? 'Note: "Pelangi Match" = LGBTQ+ community; "UWIW Match" = students or alumni of Universitas Indonesia.'
            : 'Catatan: "Pelangi Match" = komunitas LGBTQ+; "UWIW Match" = mahasiswa atau alumni Universitas Indonesia.'}
        </p>
      </motion.div>

      {/* Terms & Conditions Modal */}
      <Dialog open={showTnc} onOpenChange={setShowTnc}>
        <DialogContent className="glass-card t-text max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="t-text flex items-center gap-2">
              <ScrollText className="w-5 h-5 text-[#D98A94]" />
              {lang === 'en' ? 'Terms and Conditions' : 'Syarat dan Ketentuan'}
            </DialogTitle>
            <DialogDescription className="t-text-muted text-xs">
              {lang === 'en' ? 'Full text of the terms you are agreeing to.' : 'Isi lengkap syarat dan ketentuan yang Anda setujui.'}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <TncBody lang={lang} />
          </div>
          <Button onClick={() => setShowTnc(false)} className="w-full mt-4 bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full" data-testid="tnc-close-btn">
            {lang === 'en' ? 'Close' : 'Tutup'}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
